import { useLocation } from "react-router-dom";
import SearchableSingleSelectDropdown from "../DynamicDropdowns/SearchableSingleSelectDropdown";
import { useEffect, useState } from "react";
import { DropdownOption } from "./OrganizationFilterSection";
import Input from "../../components/Input/Input";

interface LocationFilterProps {
  setCountry: (country: DropdownOption | null) => void;
  setState: (state: DropdownOption | null) => void;
  setRegion: (region: DropdownOption | null) => void;
  setCity: (city: string) => void;
  countries: DropdownOption[];
  states: DropdownOption[];
  regions: DropdownOption[];
  selectedCountry: DropdownOption | null;
  selectedState: DropdownOption | null;
  selectedCity: string;
  isDataLoading: boolean;
  debouncedSearchCountries: (searchTerm: string) => void;
  debouncedSearchStates: (searchTerm: string) => void;
}


const LocationInfo: React.FC<LocationFilterProps> = ({
  setCountry,
  setState,
  setRegion,
  setCity,
  countries,
  states,
  regions,
  selectedCountry,
  selectedState,
  selectedCity,
  isDataLoading,
  debouncedSearchCountries,
  debouncedSearchStates,
}) => {
  const location = useLocation();
  const isCourseListingPage = location.pathname === "/course-listing";
  const [clearError, setClearError] = useState(false);

  useEffect(() => {
    const updateCity = (event: any) => {
      setCity(event.detail);
    };
    document
      .querySelector("aui-input#city")
      ?.addEventListener("auiInputChange", updateCity);
    return () => {
      document
        .querySelector("aui-input#city")
        ?.removeEventListener("auiInputChange", updateCity);
    };
  }, []);

  const handleSelectionChange = (
    value: string,
    options: DropdownOption[],
    setter: (val: DropdownOption | null) => void
  ) => {
    if (!value) {
      setter(null);
      return;
    }
    const selected = options.find((opt) => opt.name === value);
    setter(selected || null);
  };  

  const handleCountryChange = (value: string) => {
    const selected = countries.find((opt) => opt.name === value);
    setCountry(selected || null);
    setRegion(null);
    setState(null);
  };

  function addSelected(options: string[], selected: string): string[] {
    if (!selected) return options;
    return options.includes(selected) ? options : [selected, ...options];
  }

  const countryOptions = countries.map((country) => country.name ?? "");
  const selectedCountryName = selectedCountry?.name ?? "";
  const displayCountryOptions = addSelected(
    countryOptions,
    selectedCountryName
  );

  const selectedRegionName = selectedCountry?.regionName ?? "";
  const displayRegionOptions = addSelected(
    [...selectedRegionName],
    selectedRegionName
  );

  const stateOptions = states.map((state) => state.name ?? "");
  const selectedStateName = selectedState?.name ?? "";
  const displayStateOptions = addSelected(
    stateOptions,
    selectedStateName
  );
  
  return (
    <div className="row d-flex mb-2">
      <div className="col-12 col-md-4">
        <SearchableSingleSelectDropdown
          listInBtn
          data={displayCountryOptions}
          value={selectedCountryName}
          dropdownLabel="Country"
          placeHolder="Select Country"
          mainId="Country"
          isLoading={isDataLoading}
          required={!isCourseListingPage}
          onChange={handleCountryChange}
          errorText="Country is Mandatory"
          onSearch={debouncedSearchCountries}
          dropId="Country"
        />
      </div>
      <div className="col-12 col-md-4">
        <SearchableSingleSelectDropdown
          listInBtn
          data={displayRegionOptions}
          value={selectedRegionName}
          dropdownLabel="Region"
          placeHolder="Select Region"
          mainId="Region"
          isLoading={isDataLoading && !!selectedCountry?.name}
          required={!isCourseListingPage}
          onChange={(value) => {
          handleSelectionChange(value, regions, setRegion)}}
          errorText="Region is Mandatory"
          disabled={true}
          dropId="Region"
        />
      </div>
      <div className="col-12 col-md-4">
        <SearchableSingleSelectDropdown
          listInBtn
          data={displayStateOptions}
          value={selectedStateName}
          dropdownLabel="State"
          placeHolder="Select State"
          mainId="State"
          isLoading={isDataLoading && !!selectedCountry?.name}
          required={!isCourseListingPage}
          onChange={(value) => {
          handleSelectionChange(value, states, setState)}}
          errorText="State is Mandatory"
          disabled={!selectedCountry}
          onSearch={debouncedSearchStates}
          dropId="State"
        />
      </div>
      <div className="col-12 col-md-4 pt-3 mt-3">
        <div className="form-group">
          <Input
            id="city"
            label="City"
            value={selectedCity}
            placeholder="Enter City"
            required={true}
            errorMessage="City is Mandatory"
            showError={clearError && !selectedCity} // controls visibility of error
            onChange={(val) => {
              setClearError(true);
              setCity(val);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationInfo;
